const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/store/documents.ts';
import { IActionHandlersMap, KisApiAction, makeReducer, ReducerState } from '@cistec/common_web/defs/actionTypes';
import moment from 'moment/moment';
import { KISIM_TIME_FORMAT } from '@cistec/common_web/defs/generalDefs';
import {
    actionLoadPDF,
    LOAD_INVOICES,
    LOAD_PDF,
    LOAD_PDF_READ_CONFIRMATIONS,
    LOAD_REPORTS,
    LOAD_REPORTS_TO_SIGN,
    LOAD_SIGNED_REPORTS,
    loadInvoices,
    LoadPDFEndpoint,
    loadPDFReadConfirmations,
    loadReports,
    loadReportsToSign,
    loadSignedReports,
    SAVE_PDF_READ_CONFIRMATION,
    SAVE_SIGNED_PDF,
} from 'App/actions/documents';
import {
    addRequestActionHandlers,
    defaultKisApiActionSuccess,
    getData,
    getDataFromPayloadWithListObject,
    isLoading,
    removeStructure,
    useRemoteData2,
} from '@cistec/common_web/utils/redux4Requests';
import { DocumentReadWorkItem, Report } from '@cistec/common_web/defs/kisim';
import { PatAppStoreState } from './setup/rootReducer';
import { createSelector } from 'reselect';
import { getPatNrFromSession } from 'App/utils/sessionStorage';

const KEY_REPORTS = 'reportList';
const KEY_REPORTS_TO_SIGN = 'needSignature';
const KEY_SIGNED_REPORTS = 'signed';
const KEY_SAVE_SIGNED_REPORTS = 'saveSigned';
const KEY_INVOICES = 'invoiceList';
const KEY_PDF = 'PDF';
const KEY_PDF_READ = 'readConfirmation';
const KEY_PDFS_READ = 'readConfirmations';

const getLoadPDFKeys = (action: KisApiAction) => [KEY_PDF, action.meta.endpoint, action.meta.code, action.meta.sender];

const getLoadPDFData = (action: KisApiAction) => {
    const { payload } = action;

    if (payload.result) {
        return payload.result;
    } else if (payload.Buffer) {
        // "Buffer" is from Synedra (invoices)
        // DEV-128: Base64 contains inconform CRLFs
        return payload.Buffer.replace(/[\n\r]/g, '');
    } else if (payload.List?.['1']?.['CONTENT']) {
        return payload.List?.['1']?.['CONTENT']?.replace(/[\n\r]/g, '');
    }

    return undefined;
};

const createSaveSignedPDFKey = (action: KisApiAction) => [
    KEY_SAVE_SIGNED_REPORTS,
    action.meta.sender,
    action.meta.pdfNumber,
];

const handleSaveSignedPDFSuccess = (state: ReducerState, action: KisApiAction) => {
    // clear data for documents to be signed and signed documents so the data will be loaded after a successful save
    state = removeStructure(state, [KEY_REPORTS_TO_SIGN]);
    state = removeStructure(state, [KEY_SIGNED_REPORTS]);
    state = defaultKisApiActionSuccess(state, action.payload, createSaveSignedPDFKey(action));
    return state;
};

const handlePDFReadConfirmationsSuccess = (state: ReducerState, action: KisApiAction) => {
    const workItems = getDataFromPayloadWithListObject(action);
    workItems.forEach((workItem: DocumentReadWorkItem) => {
        const TKEY = workItem.TKEY;
        const reportNr = TKEY.split(';')[1];
        if (reportNr) {
            state = defaultKisApiActionSuccess(state, workItem, [KEY_PDF_READ, reportNr]);
        }
    });
    // Handle successful load of data, but the data is stored somewhere else
    state = defaultKisApiActionSuccess(state, undefined, [KEY_PDFS_READ]);
    return state;
};

const getSavePDFReadConfirmationKeys = (action: KisApiAction) => [KEY_PDF_READ, action.meta.reportNr];

export function getSavePDFReadConfirmationData(action: KisApiAction) {
    // Service for marking PatReport as read returns result prop containing WorkItem
    // Service for saving WorkItem returns WorkItem directly in payload
    return action.payload.result || action.payload;
}

const handlers: IActionHandlersMap = {};

addRequestActionHandlers(handlers, LOAD_PDF, getLoadPDFKeys, getLoadPDFData);
addRequestActionHandlers(handlers, LOAD_REPORTS, [KEY_REPORTS], getDataFromPayloadWithListObject);
addRequestActionHandlers(handlers, LOAD_REPORTS_TO_SIGN, [KEY_REPORTS_TO_SIGN], getDataFromPayloadWithListObject);
addRequestActionHandlers(handlers, LOAD_SIGNED_REPORTS, [KEY_SIGNED_REPORTS], getDataFromPayloadWithListObject);
addRequestActionHandlers(handlers, LOAD_INVOICES, [KEY_INVOICES]);
addRequestActionHandlers(handlers, SAVE_SIGNED_PDF, createSaveSignedPDFKey, undefined, handleSaveSignedPDFSuccess);
addRequestActionHandlers(
    handlers,
    LOAD_PDF_READ_CONFIRMATIONS,
    [KEY_PDFS_READ],
    undefined,
    handlePDFReadConfirmationsSuccess
);
addRequestActionHandlers(
    handlers,
    SAVE_PDF_READ_CONFIRMATION,
    getSavePDFReadConfirmationKeys,
    getSavePDFReadConfirmationData
);

export const documentsReducer = makeReducer(handlers);

// SELECTORS
const selectReducerState = (state: PatAppStoreState) => state.documents;

const selectReports = (state: PatAppStoreState): Report[] | undefined => getData(state.documents, [KEY_REPORTS]);

const selectReportsToSign = (state: PatAppStoreState) => getData(state.documents, [KEY_REPORTS_TO_SIGN]);

const selectSignedReports = (state: PatAppStoreState) => getData(state.documents, [KEY_SIGNED_REPORTS]);

export const makeSelectIsSavingSignedReport = (sender: string, pdfNumber: string) => (state: PatAppStoreState) =>
    isLoading(state.documents, [KEY_SAVE_SIGNED_REPORTS, sender, pdfNumber]);

export const makeSelectSignedReportInfo = (sender: string, pdfNumber: string) => (state: PatAppStoreState) =>
    getData(state.documents, [KEY_SAVE_SIGNED_REPORTS, sender, pdfNumber]);

const selectInvoices = (state: PatAppStoreState): Report[] | undefined => getData(state.documents, [KEY_INVOICES]);

export const makeSelectPDF = (endpoint: LoadPDFEndpoint, code: string, sender: string) =>
    createSelector([selectReducerState], (state: ReducerState) => getData(state, [KEY_PDF, endpoint, code, sender]));

const makeSelectPDFReadConfirmation = (reportNr: string) =>
    createSelector([selectReducerState], (state: ReducerState): DocumentReadWorkItem | undefined =>
        getData(state, [KEY_PDF_READ, reportNr])
    );
export const makeSelectIsLoadingPDFReadConfirmation = (reportNr: string) =>
    createSelector([selectReducerState], (state: ReducerState) => isLoading(state, [KEY_PDF_READ, reportNr]));

// HOOKS

const timeRangeToLookMinMax = () => {
    const mindat = moment().add(-10, 'years').format(KISIM_TIME_FORMAT);
    const maxdat = moment().format(KISIM_TIME_FORMAT);
    return { mindat, maxdat };
};

export function useReports() {
    const patnr = getPatNrFromSession();
    return useRemoteData2('documents', {
        canRequest: !!patnr,
        request: dispatch => {
            const { mindat, maxdat } = timeRangeToLookMinMax();
            dispatch(loadReports(mindat, maxdat));
        },
        select: selectReports,
        keys: [KEY_REPORTS],
    });
}

export function useReportsToSign() {
    const patnr = getPatNrFromSession();
    return useRemoteData2('documents', {
        canRequest: !!patnr,
        request: dispatch => dispatch(loadReportsToSign()),
        select: selectReportsToSign,
        keys: [KEY_REPORTS_TO_SIGN],
    });
}

export function useSignedReports() {
    const patnr = getPatNrFromSession();
    return useRemoteData2('documents', {
        canRequest: !!patnr,
        request: dispatch => dispatch(loadSignedReports()),
        select: selectSignedReports,
        keys: [KEY_SIGNED_REPORTS],
    });
}

export function useInvoices() {
    const patnr = getPatNrFromSession();
    return useRemoteData2('documents', {
        canRequest: !!patnr,
        request: dispatch => dispatch(loadInvoices()),
        select: selectInvoices,
        keys: [KEY_INVOICES],
    });
}

export function usePDF(endpoint: LoadPDFEndpoint, code: string, sender: string) {
    const patnr = getPatNrFromSession();
    return useRemoteData2('documents', {
        canRequest: !!patnr && !!sender && !!code,
        request: dispatch => dispatch(actionLoadPDF(endpoint, code, sender)),
        select: makeSelectPDF(endpoint, code, sender),
        keys: [KEY_PDF, endpoint, code, sender],
    });
}

export const usePDFReadConfirmation = (reportNr: string) => {
    const patnr = getPatNrFromSession();
    return useRemoteData2('documents', {
        canRequest: !!patnr,
        request: dispatch => dispatch(loadPDFReadConfirmations()),
        keys: [KEY_PDFS_READ],
        select: makeSelectPDFReadConfirmation(reportNr),
    });
};
